<script setup>
import { computed, isReactive } from 'vue';
import {
    BuildingStorefrontIcon,
    PhoneArrowUpRightIcon,
    CalendarIcon,
} from '@heroicons/vue/24/outline';
import moment from 'moment-timezone';

const props = defineProps({
    order: {
        type: Object,
        required: true,
    },
    locations: {
        type: Array,
        required: true,
    },
    fulfillmentDisplay: {
        type: String,
        required: true,
    },
    isCatering: {
        type: Boolean,
        required: true,
    },
});

const locationName = computed(() => props.locations[props.order.location_id - 1]?.name);
const locationPhoneNumber = computed(
    () => props.locations[props.order.location_id - 1]?.address.phoneDisplay,
);
const locationAddress = computed(
    () =>
        `${props.locations[props.order.location_id - 1]?.address.street},
        ${props.locations[props.order.location_id - 1]?.address.city},
        ${props.locations[props.order.location_id - 1]?.address.state},
        ${props.locations[props.order.location_id - 1]?.address.zip}`,
);

const orderTime = computed(() => {
    if (props.order.ready_at) {
        const parsedDate = moment.utc(props.order.ready_at).tz('America/Chicago');
        const startOfToday = moment().startOf('day');
        const endOfToday = moment().endOf('day');

        if (parsedDate < startOfToday || parsedDate > endOfToday) {
            return parsedDate.format('dddd, MMMM Do YYYY, h:mm a');
        } else {
            return parsedDate.format('[Today], h:mm a');
        }
    }

    return null;
});
</script>

<template>
    <div
        class="relative z-20 h-fit w-full overflow-hidden bg-palmers-white sm:rounded-2xl sm:border sm:shadow-md"
    >
        <div
            class="text-md flex w-full flex-col items-center bg-palmers-havarti py-3 font-bold text-palmers-charcoal-grey"
        >
            <p class="uppercase">
                {{ isCatering ? 'Estimated Fulfillment' : 'Order Details' }}
            </p>
        </div>
        <div class="w-full space-y-6 px-8 py-5 pt-5 text-lg shadow-inner">
            <div v-if="order.ready_at" class="flex w-full items-center gap-x-6">
                <div class="min-w-10">
                    <CalendarIcon class="size-10 text-palmers-charcoal-grey" />
                </div>
                <p class="text-palmers-charcoal-grey">
                    <span class="font-semibold">{{ fulfillmentDisplay }}</span>
                    {{ orderTime }}
                </p>
            </div>
            <div v-else class="flex w-full items-center gap-x-[1rem]">
                <!-- Wouldn't work as import? -->
                <!-- <AsapIcon class="size-10 text-palmers-charcoal-grey" /> -->
                <div class="min-w-10">
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 122.644 106.175"
                        aria-hidden="true"
                        fill="currentColor"
                        class="size-11 -translate-x-1.5 text-palmers-charcoal-grey"
                    >
                        <path
                            d="M122.4,76.114l1.479-1.479a4.162,4.162,0,0,0,0-5.879l-5.248-5.248a4.161,4.161,0,0,0-5.879,0l-1.479,1.478a45.2,45.2,0,0,0-18.2-7.559V53.31h4.089a4.162,4.162,0,0,0,4.157-4.157V46.6a4.162,4.162,0,0,0-4.157-4.157H73.252A4.162,4.162,0,0,0,69.095,46.6v2.555a4.162,4.162,0,0,0,4.157,4.157H77.34v4.118a45.2,45.2,0,0,0-18.2,7.559l-1.479-1.479a4.162,4.162,0,0,0-5.879,0l-5.248,5.248a4.162,4.162,0,0,0,0,5.879l1.479,1.479a45.436,45.436,0,1,0,74.387,0Zm-7.211-10.168a.711.711,0,0,1,1,0l5.248,5.248a.71.71,0,0,1,0,1L120.3,73.345a45.838,45.838,0,0,0-6.252-6.252ZM72.542,49.153V46.6a.711.711,0,0,1,.71-.71H97.166a.711.711,0,0,1,.71.71v2.555a.711.711,0,0,1-.71.71H73.252A.711.711,0,0,1,72.542,49.153Zm8.246,4.157h8.841v3.65q-2.183-.212-4.421-.215t-4.421.215ZM48.974,72.2a.71.71,0,0,1,0-1l5.248-5.248a.71.71,0,0,1,1,0l1.147,1.147a45.839,45.839,0,0,0-6.252,6.252Zm36.234,71.971A41.988,41.988,0,1,1,127.2,102.18,42.036,42.036,0,0,1,85.209,144.169Zm0-77.779A35.791,35.791,0,1,0,121,102.181,35.831,35.831,0,0,0,85.209,66.39Zm0,68.134a32.343,32.343,0,1,1,32.343-32.343A32.38,32.38,0,0,1,85.209,134.524Zm16.047-56.69-2.442,4.229a1.724,1.724,0,1,1-2.985-1.724L98.27,76.11a1.724,1.724,0,1,1,2.985,1.724Zm3.439,13.1a1.724,1.724,0,0,1,.631-2.355l4.229-2.442a1.724,1.724,0,0,1,1.724,2.985l-4.229,2.442a1.723,1.723,0,0,1-2.355-.631Zm11.346,11.25a1.724,1.724,0,0,1-1.724,1.724h-4.884a1.724,1.724,0,0,1,0-3.447h4.884a1.724,1.724,0,0,1,1.724,1.724ZM111.91,117.6a1.723,1.723,0,0,1-2.355.631l-4.229-2.442a1.724,1.724,0,0,1,1.724-2.985l4.229,2.442A1.723,1.723,0,0,1,111.91,117.6Zm-10.654,8.931a1.724,1.724,0,1,1-2.985,1.724l-2.442-4.23a1.724,1.724,0,0,1,2.985-1.724ZM86.932,126.4v4.884a1.724,1.724,0,0,1-3.447,0V126.4a1.724,1.724,0,0,1,3.447,0Zm-12.343-2.384-2.442,4.23a1.724,1.724,0,0,1-2.986-1.724L71.6,122.3a1.724,1.724,0,0,1,2.986,1.724Zm-8.867-10.59a1.724,1.724,0,0,1-.631,2.355l-4.229,2.442a1.724,1.724,0,0,1-1.724-2.985l4.229-2.442A1.724,1.724,0,0,1,65.722,113.431ZM60.984,103.9H56.1a1.724,1.724,0,1,1,0-3.447h4.884a1.724,1.724,0,0,1,0,3.447ZM65.722,90.93a1.723,1.723,0,0,1-2.355.631l-4.229-2.442a1.724,1.724,0,1,1,1.724-2.985l4.229,2.442A1.724,1.724,0,0,1,65.722,90.93Zm8.867-10.59A1.724,1.724,0,1,1,71.6,82.063l-2.442-4.229a1.724,1.724,0,1,1,2.985-1.724ZM86.932,97.588V84.619a1.724,1.724,0,0,0-3.447,0V97.588a4.911,4.911,0,1,0,3.447,0Zm-1.724,6.056a1.463,1.463,0,1,1,1.463-1.463A1.465,1.465,0,0,1,85.209,103.644ZM83.485,77.956V73.072a1.724,1.724,0,1,1,3.447,0v4.884a1.724,1.724,0,1,1-3.447,0ZM15.438,96.617a1.724,1.724,0,0,1,1.724-1.724H33.389a1.724,1.724,0,0,1,0,3.447H17.161A1.724,1.724,0,0,1,15.438,96.617ZM37.648,85.491a1.724,1.724,0,0,1-1.724,1.724H21.771a1.724,1.724,0,0,1,0-3.447H35.924A1.724,1.724,0,0,1,37.648,85.491Zm-4.162,23.977H10.724a1.724,1.724,0,0,1,0-3.447H33.486a1.724,1.724,0,0,1,0,3.447Zm4.162,9.4a1.724,1.724,0,0,1-1.724,1.724h-15.1a1.724,1.724,0,0,1,0-3.447h15.1A1.724,1.724,0,0,1,37.648,118.87Z"
                            transform="translate(-8.5 -41.941)"
                            stroke="currentColor"
                            stroke-width="1"
                        />
                    </svg>
                </div>
                <p class="text-palmers-charcoal-grey">
                    <span class="font-semibold">{{ fulfillmentDisplay }}</span> ASAP
                </p>
            </div>
            <div class="flex w-full items-center gap-x-6">
                <div class="min-w-10">
                    <BuildingStorefrontIcon
                        class="size-10 stroke-[1.2] text-palmers-charcoal-grey"
                    />
                </div>
                <div class="text-lg">
                    <p class="font-semibold text-palmers-charcoal-grey">{{ locationName }}</p>
                    <a
                        class="cursor-pointer text-palmers-charcoal-grey underline active:opacity-60 supports-hover:hover:opacity-80"
                        target="_blank"
                        :href="`https://maps.google.com?q=${encodeURI(locationAddress)}`"
                    >
                        {{ locationAddress }}
                    </a>
                </div>
            </div>
            <div class="flex w-full items-center gap-x-6 text-lg">
                <div class="min-w-10">
                    <PhoneArrowUpRightIcon class="size-9 text-palmers-charcoal-grey" />
                </div>
                <p class="hidden text-palmers-charcoal-grey sm:block">{{ locationPhoneNumber }}</p>
                <a
                    :href="`tel:${locationPhoneNumber}`"
                    class="block text-palmers-charcoal-grey underline active:opacity-60 sm:hidden"
                    >{{ locationPhoneNumber }}</a
                >
            </div>
        </div>
    </div>
</template>
